import { useEffect, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import {
  KIND_LABEL,
  KIND_LABEL_KEY,
  PRIORITY_LABEL,
  PRIORITY_LABEL_KEY,
  STATE_LABEL,
  STATE_LABEL_KEY,
  ageSince,
  useApprovals,
  useClaimApproval,
  useDecideApproval,
  useReleaseApproval,
  type AttendantDecisionOutcome,
  type AttendantRequestPriority,
  type AttendantRequestState,
  type AttendantRequestSummary,
} from '@/hooks/use-approvals';
import { useAuthStore } from '@/auth/auth-store';
import { useT } from '@/i18n/use-t';
import { cn } from '@/lib/utils';

const HISTORICAL_STATES: AttendantRequestState[] = ['approved', 'declined', 'escalated', 'expired'];

function priorityVariant(p: AttendantRequestPriority) {
  switch (p) {
    case 'P1':
      return 'destructive' as const;
    case 'P2':
      return 'destructive' as const;
    case 'P3':
      return 'default' as const;
    case 'P4':
      return 'outline' as const;
    case 'P5':
    default:
      return 'outline' as const;
  }
}

function stateVariant(s: AttendantRequestState) {
  switch (s) {
    case 'approved':
      return 'success' as const;
    case 'declined':
      return 'destructive' as const;
    case 'escalated':
      return 'destructive' as const;
    case 'expired':
      return 'outline' as const;
    case 'claimed':
      return 'default' as const;
    case 'pending':
    default:
      return 'outline' as const;
  }
}

export function ApprovalsPage() {
  const t = useT('admin_ui');
  const [filter, setFilter] = useState<'operational' | 'historical'>('operational');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // 1Hz tick to refresh age column without re-fetching.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const query = useApprovals({
    pageSize: 200,
    state:
      filter === 'operational'
        ? (['pending', 'claimed'] as AttendantRequestState[])
        : HISTORICAL_STATES,
  });

  const items = query.data?.items ?? [];
  const selected = useMemo(
    () => items.find((it) => it.id === selectedId) ?? null,
    [items, selectedId],
  );

  const stateFilters: { value: 'operational' | 'historical'; label: string }[] = [
    {
      value: 'operational',
      label: t('approvals.filter.operational', 'Open queue (pending + claimed)'),
    },
    { value: 'historical', label: t('approvals.filter.historical', 'Historical') },
  ];

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">{t('approvals.queue.title', 'Approval queue')}</h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'approvals.queue.description',
            'Self-checkout attendant approvals. P1 (age-restricted) requests block the customer at payment; P5 (generic calls) are routine. Claim a request to prevent another attendant from acting on it.',
          )}
        </p>
      </header>

      <div className="flex items-center justify-between rounded-md border border-border bg-card p-1 text-sm">
        <div className="flex items-center gap-1">
          {stateFilters.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={cn(
                'rounded-sm px-3 py-1.5 transition-colors',
                filter === opt.value
                  ? 'bg-secondary font-medium text-secondary-foreground'
                  : 'text-muted-foreground hover:bg-secondary/60',
              )}
              onClick={() => {
                setFilter(opt.value);
                setSelectedId(null);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          {query.isFetching
            ? t('approvals.queue.refreshing', 'Refreshing…')
            : t('approvals.queue.refresh', 'Refresh')}
        </Button>
      </div>

      <div className="grid grid-cols-[1fr_420px] gap-5">
        <div className="rounded-md border border-border bg-card">
          {query.isLoading ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">
              {t('common.loading', 'Loading…')}
            </p>
          ) : items.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">
              {filter === 'operational'
                ? t('approvals.queue.empty', 'No open approvals. The queue is empty.')
                : t('approvals.queue.emptyHistorical', 'No historical approvals match.')}
            </p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>{t('approvals.col.priority', 'Priority')}</TH>
                  <TH>{t('approvals.col.kind', 'Kind')}</TH>
                  <TH>{t('approvals.col.age', 'Age')}</TH>
                  <TH>{t('approvals.col.state', 'State')}</TH>
                  <TH>{t('approvals.field.terminal', 'Terminal')}</TH>
                  <TH className="text-right">{t('approvals.col.actions', 'Actions')}</TH>
                </TR>
              </THead>
              <TBody>
                {items.map((it) => (
                  <RequestRow
                    key={it.id}
                    request={it}
                    now={now}
                    selected={it.id === selectedId}
                    onSelect={() => setSelectedId(it.id)}
                  />
                ))}
              </TBody>
            </Table>
          )}
        </div>

        <aside className="rounded-md border border-border bg-card p-4">
          {selected ? (
            <RequestDetail request={selected} />
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('approvals.detail.selectPrompt', 'Select a request to see details.')}
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}

function RequestRow({
  request,
  now,
  selected,
  onSelect,
}: {
  request: AttendantRequestSummary;
  now: number;
  selected: boolean;
  onSelect: () => void;
}) {
  const t = useT('admin_ui');
  return (
    <TR
      role="button"
      onClick={onSelect}
      className={cn('cursor-pointer hover:bg-secondary/50', selected && 'bg-secondary/60')}
    >
      <TD>
        <Badge variant={priorityVariant(request.priority)}>
          {t(PRIORITY_LABEL_KEY[request.priority], PRIORITY_LABEL[request.priority])}
        </Badge>
      </TD>
      <TD>{t(KIND_LABEL_KEY[request.kind], KIND_LABEL[request.kind])}</TD>
      <TD className="font-mono text-xs">{ageSince(request.requestedAt, now)}</TD>
      <TD>
        <Badge variant={stateVariant(request.state)}>
          {t(STATE_LABEL_KEY[request.state], STATE_LABEL[request.state])}
        </Badge>
      </TD>
      <TD className="font-mono text-xs">{request.terminalId.slice(-8)}</TD>
      <TD className="text-right">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
        >
          {t('approvals.queue.open', 'Open')}
        </Button>
      </TD>
    </TR>
  );
}

function RequestDetail({ request }: { request: AttendantRequestSummary }) {
  const t = useT('admin_ui');
  const myUserId = useAuthStore((s) => s.user?.id ?? null);
  const claim = useClaimApproval();
  const release = useReleaseApproval();
  const decide = useDecideApproval();

  const [notes, setNotes] = useState('');
  const [banner, setBanner] = useState<string | null>(null);

  // Reset notes + banner when selection switches.
  useEffect(() => {
    setNotes('');
    setBanner(null);
  }, [request.id]);

  const iAmClaimant = request.claimedByUserId !== null && request.claimedByUserId === myUserId;
  const isPending = request.state === 'pending';
  const isClaimed = request.state === 'claimed';
  const isTerminal =
    request.state === 'approved' ||
    request.state === 'declined' ||
    request.state === 'escalated' ||
    request.state === 'expired';

  async function runClaim() {
    setBanner(null);
    try {
      await claim.mutateAsync(request.id);
    } catch (err) {
      setBanner(extractError(err) ?? t('approvals.error.claim', 'Claim failed.'));
    }
  }

  async function runRelease() {
    setBanner(null);
    try {
      await release.mutateAsync(request.id);
    } catch (err) {
      setBanner(extractError(err) ?? t('approvals.error.release', 'Release failed.'));
    }
  }

  async function runDecide(outcome: AttendantDecisionOutcome) {
    setBanner(null);
    try {
      await decide.mutateAsync({
        id: request.id,
        outcome,
        notes: notes.trim().length > 0 ? notes.trim() : undefined,
      });
      setNotes('');
    } catch (err) {
      setBanner(extractError(err) ?? t('approvals.error.decision', 'Decision failed.'));
    }
  }

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <Badge variant={priorityVariant(request.priority)}>
            {t(PRIORITY_LABEL_KEY[request.priority], PRIORITY_LABEL[request.priority])}
          </Badge>
          <Badge variant={stateVariant(request.state)}>
            {t(STATE_LABEL_KEY[request.state], STATE_LABEL[request.state])}
          </Badge>
        </div>
        <h2 className="text-base font-semibold">
          {t(KIND_LABEL_KEY[request.kind], KIND_LABEL[request.kind])}
        </h2>
      </header>

      <dl className="space-y-1 text-sm">
        <Row label={t('approvals.detail.requestId', 'Request id')} value={request.id} mono />
        <Row label={t('approvals.field.terminal', 'Terminal')} value={request.terminalId} mono />
        <Row
          label={t('approvals.detail.requestedAt', 'Requested at')}
          value={new Date(request.requestedAt).toLocaleString()}
        />
        {request.claimedByUserId !== null && (
          <Row
            label={t('approvals.detail.claimedBy', 'Claimed by')}
            value={request.claimedByUserId}
            mono
          />
        )}
        {request.decisionOutcome !== null && (
          <Row
            label={t('approvals.detail.decided', 'Decided')}
            value={`${request.decisionOutcome} · ${new Date(request.decisionAt ?? '').toLocaleString()}`}
          />
        )}
      </dl>

      <details className="rounded border border-border bg-secondary/30 p-2 text-xs">
        <summary className="cursor-pointer text-muted-foreground">
          {t('approvals.detail.payload', 'Payload (JSON)')}
        </summary>
        <pre className="mt-2 overflow-x-auto font-mono text-[11px]">
          {JSON.stringify(request.payload, null, 2)}
        </pre>
      </details>

      {banner !== null && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {banner}
        </p>
      )}

      <div className="space-y-2">
        {isPending && (
          <Button onClick={runClaim} disabled={claim.isPending}>
            {claim.isPending
              ? t('approvals.detail.claiming', 'Claiming…')
              : t('approvals.detail.claim', 'Claim')}
          </Button>
        )}
        {isClaimed && iAmClaimant && (
          <>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t(
                'approvals.detail.notesPlaceholder',
                'Decision notes (optional, ≤1000 chars)',
              )}
              rows={3}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => runDecide('approve')}
                disabled={decide.isPending}
                variant="default"
              >
                {t('approvals.detail.approve', 'Approve')}
              </Button>
              <Button
                onClick={() => runDecide('decline')}
                disabled={decide.isPending}
                variant="outline"
              >
                {t('approvals.detail.decline', 'Decline')}
              </Button>
              <Button
                onClick={() => runDecide('escalate')}
                disabled={decide.isPending}
                variant="outline"
              >
                {t('approvals.detail.escalate', 'Escalate')}
              </Button>
              <Button onClick={runRelease} disabled={release.isPending} variant="ghost">
                {t('approvals.detail.release', 'Release')}
              </Button>
            </div>
          </>
        )}
        {isClaimed && !iAmClaimant && (
          <p className="text-xs text-muted-foreground">
            {t(
              'approvals.detail.claimedByOther',
              'Claimed by another attendant. Wait or ask them to release before acting.',
            )}
          </p>
        )}
        {isTerminal && (
          <p className="text-xs text-muted-foreground">
            {t(
              'approvals.detail.terminalState',
              'This request is in a terminal state and cannot be acted on.',
            )}
          </p>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-2">
      <dt className="w-32 shrink-0 text-muted-foreground">{label}</dt>
      <dd className={cn('flex-1 break-all', mono === true && 'font-mono text-xs')}>{value}</dd>
    </div>
  );
}

function extractError(err: unknown): string | null {
  if (isAxiosError(err)) {
    const body = err.response?.data;
    if (body !== undefined && body !== null && typeof body === 'object') {
      const obj = body as { message?: string; code?: string };
      if (typeof obj.message === 'string' && obj.message.length > 0) {
        return obj.code !== undefined ? `${obj.code}: ${obj.message}` : obj.message;
      }
    }
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return null;
}
